import React, { useEffect, useMemo, useRef, useState } from 'react'
import { supabase } from '../../supabaseClient'
import Swal from 'sweetalert2'
import Header from '../../components/Header'
import { useNavigate } from 'react-router-dom'

if (typeof document !== 'undefined' && !document.getElementById('shimmer-keyframes')) {
    const style = document.createElement('style')
    style.id = 'shimmer-keyframes'
    style.innerHTML = `
        @keyframes shimmer {
            0% { background-position: -400px 0; }
            100% { background-position: 400px 0; }
        }
    `
    document.head.appendChild(style)
}

// ─── Design tokens ────────────────────────────────────────────────────
const C = {
    espresso: 'transparent',
    dark: '#F9FAFB',
    card: '#FFFFFF',
    border: '#E5E7EB',
    muted: '#9CA3AF',
    text: '#111827',
    textSoft: '#6B7280',
    amber: '#D97706',
    gold: '#B45309',
    teal: '#0D9488',
    coral: '#DC2626',
    blue: '#2563EB',
    green: '#16A34A',
    purple: '#7C3AED',
    indigo: '#4F46E5',
    nude: '#A0826D',
    midgrey: '#6B7280',
}

const SkeletonBlock = ({ className = '', style = {} }) => (
    <div
        className={`rounded ${className}`}
        style={{
            backgroundImage: 'linear-gradient(90deg, #e5e7eb 0px, #f3f4f6 40px, #e5e7eb 80px)',
            backgroundSize: '400px 100%',
            animation: 'shimmer 1.4s ease-in-out infinite',
            ...style,
        }}
    />
)

export default function OrderManagementAdmin({ onGoToProfile }) {
    const [orders, setOrders] = useState([])
    const [loading, setLoading] = useState(true)
    const [branches, setBranches] = useState([])
    const [selectedBranchId, setSelectedBranchId] = useState('all')

    const [searchQuery, setSearchQuery] = useState('')
    const [filterStatus, setFilterStatus] = useState('All')
    const [dateFilter, setDateFilter] = useState('all')

    const [showViewModal, setShowViewModal] = useState(false)
    const [viewingOrder, setViewingOrder] = useState(null)
    const [orderItems, setOrderItems] = useState([])
    const [showVoidedItems, setShowVoidedItems] = useState(false)

    const [customStart, setCustomStart] = useState('')
    const [customEnd, setCustomEnd] = useState('')

    useEffect(() => {
        try {
            const raw = sessionStorage.getItem('dashboardFilterHandoff')
            if (raw) {
                const handoff = JSON.parse(raw)
                if (handoff.branchId) setSelectedBranchId(handoff.branchId)

                if (handoff.preset === '1d') {
                    // "Today" on the dashboard maps directly to Order Management's own "Today" filter
                    setDateFilter('day')
                    setCustomStart('')
                    setCustomEnd('')
                } else if (handoff.customStart && handoff.customEnd) {
                    setDateFilter('custom')
                    setCustomStart(handoff.customStart)
                    setCustomEnd(handoff.customEnd)
                }
                sessionStorage.removeItem('dashboardFilterHandoff')
            }
        } catch (err) {
            console.error('Failed to apply dashboard filter handoff:', err)
        }
    }, [])

    const [pendingPage, setPendingPage] = useState(1)
    const [preparingPage, setPreparingPage] = useState(1)
    const [readyPage, setReadyPage] = useState(1)
    const [completedPage, setCompletedPage] = useState(1)
    const [cancelledPage, setCancelledPage] = useState(1)
    const [filteredPage, setFilteredPage] = useState(1)
    const dragScrollRef = useRef({ isDown: false, startX: 0, scrollLeft: 0, moved: false })

    const handleDragMouseDown = (e) => {
        const el = e.currentTarget
        dragScrollRef.current = {
            isDown: true,
            startX: e.pageX - el.offsetLeft,
            scrollLeft: el.scrollLeft,
            moved: false
        }
        el.classList.add('cursor-grabbing')
    }

    const handleDragMouseMove = (e) => {
        const state = dragScrollRef.current
        if (!state.isDown) return
        e.preventDefault()
        const el = e.currentTarget
        const x = e.pageX - el.offsetLeft
        const walk = x - state.startX
        if (Math.abs(walk) > 3) state.moved = true
        el.scrollLeft = state.scrollLeft - walk
    }

    const handleDragMouseUpOrLeave = (e) => {
        dragScrollRef.current.isDown = false
        e.currentTarget.classList.remove('cursor-grabbing')
    }

    const handleHorizontalWheelScroll = (e) => {
        if (e.deltaY === 0) return
        e.currentTarget.scrollLeft += e.deltaY
        e.preventDefault()
    }

    const navigate = useNavigate();
    const PAGE_SIZE = 12

    useEffect(() => {
        const style = document.createElement('style')
        style.innerHTML = `
            .swal2-confirm { background-color: #FF6F3C !important; border: none !important; }
            .swal2-confirm:hover { background-color: #ff4c0b !important; }
            .swal2-cancel { background-color: #6b7280 !important; border: none !important; }
            .swal2-cancel:hover { background-color: #5a5a5a !important; }
        `
        document.head.appendChild(style)
        return () => document.head.removeChild(style)
    }, [])

    useEffect(() => {
        fetchBranches()
        fetchOrders()

        const channel = supabase
            .channel('admin-orders-channel')
            .on('postgres_changes', { event: '*', schema: 'public', table: 'orders' }, () => {
                fetchOrders()
            })
            .subscribe()

        return () => supabase.removeChannel(channel)
    }, [])

    useEffect(() => {
        setPendingPage(1);
        setPreparingPage(1);
        setReadyPage(1);
        setCompletedPage(1);
        setCancelledPage(1)
        setFilteredPage(1)
    }, [searchQuery, filterStatus, dateFilter, customStart, customEnd])

    useEffect(() => {
        setPendingPage(1);
        setPreparingPage(1);
        setReadyPage(1);
        setCompletedPage(1);
        setCancelledPage(1);
        setFilteredPage(1)
    }, [selectedBranchId])

    // Icons
    const PendingIcon = () => (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 34 34"
            fill="none"
            className="w-5 h-5"
        >
            <path
                d="M1.5 16.8947C1.5 18.9163 1.8982 20.9182 2.67185 22.786C3.44551 24.6537 4.57947 26.3508 6.009 27.7804C7.43853 29.2099 9.13562 30.3439 11.0034 31.1175C12.8712 31.8912 14.873 32.2894 16.8947 32.2894C18.9163 32.2894 20.9182 31.8912 22.786 31.1175C24.6537 30.3439 26.3508 29.2099 27.7804 27.7804C29.2099 26.3508 30.3439 24.6537 31.1175 22.786C31.8912 20.9182 32.2894 18.9163 32.2894 16.8947C32.2894 12.8118 30.6674 8.89606 27.7804 6.009C24.8933 3.12194 20.9776 1.5 16.8947 1.5C12.8118 1.5 8.89606 3.12194 6.009 6.009C3.12193 8.89606 1.5 12.8118 1.5 16.8947Z"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
            <path
                d="M16.8945 8.3418V16.8944L22.0261 22.026"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    )

    const PreparingIcon = () => (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 91 91"
            fill="currentColor"
            className="w-5 h-5"
        >
            <path d="M45.5 17.7734C44.0781 17.7734 42.6562 18.4844 39.8125 19.9062L41.7406 25.6903C31.9326 26.1671 20.0413 28.4446 17.5388 32.5254H73.4585C72.2122 30.4953 68.6431 28.9113 64.1621 27.7731V23.9941H60.9629V27.0629C57.1944 26.3329 53.0715 25.8756 49.2594 25.6903L51.1875 19.9062C48.3438 18.4844 46.9219 17.7734 45.5 17.7734ZM17.0625 35.7246V38.2129H9.77539V41.4121H17.0625V68.25C17.0625 71.0938 19.9062 73.9375 22.75 73.9375H68.25C71.0938 73.9375 73.9375 71.0938 73.9375 68.25V41.4121H81.2246V38.2129H73.9375V35.7246H17.0625Z" />
        </svg>
    )


    // Fetch
    const fetchBranches = async () => {
        try {
            const { data, error } = await supabase
                .from('branches')
                .select('branch_id, name, status')
                .neq('status', 'inactive')
                .order('name', { ascending: true })
            if (error) throw error
            setBranches(data || [])
        } catch (err) {
            console.error('Error fetching branches:', err)
        }
    }

    const fetchOrders = async () => {
        try {
            setLoading(true)
            const { data, error } = await supabase
                .from('orders')
                .select(`
                    order_id,
                    user_id,
                    reference_number,
                    branch_id,
                    order_source,
                    fulfillment_type,
                    order_status,
                    subtotal,
                    discount,
                    tax,
                    final_amount,
                    created_at,
                    updated_at,
                    completed_at,
                    address_id,
                    customer_notes,
                    branches (
                        branch_id,
                        name
                    ),
                     delivery_address:customer_addresses!orders_address_id_fkey ( label, full_address ),
                    customer:customers!orders_user_id_fkey ( first_name, last_name, phone_number, email ),
                    barista_info:barista_info!orders_order_completed_by_fkey ( barista_id, first_name, last_name )
                `)
                .order('created_at', { ascending: false })

            if (error) throw error
            setOrders(data || [])
        } catch (err) {
            console.error('Error fetching orders:', err)
            Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to load orders.', confirmButtonColor: '#FF6F3C' })
        } finally {
            setLoading(false)
        }
    }

    const handleViewOrder = async (order) => {
        setViewingOrder(order)
        try {
            const { data, error } = await supabase
                .from('order_items')
                .select(`
                    *,
                    menu_items (
                        name,
                        category,
                        item_img,
                        classification
                    ),
                    order_item_variants (
                        variant_id,
                        variant_price,
                        item_variants (
                            variant_id,
                            variant_name,
                            category
                        )
                    ),
                    order_item_modifiers (
                        modifier_id,
                        modifier_price,
                        item_modifiers (
                            modifier_id,
                            modifier_name,
                            category
                        )
                    )
                `)
                .eq('order_id', order.order_id)
            if (error) throw error
            setOrderItems(data || [])
        } catch (err) {
            console.error('Error fetching order items:', err)
            setOrderItems([])
        }
        setShowViewModal(true)
    }

    const handleStatusChange = async (orderId, newStatus, branchId) => {
        const result = await Swal.fire({
            title: 'Update Order Status?',
            text: `Change order status to "${newStatus}"?`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#FF6F3C',
            cancelButtonColor: '#6b7280',
            confirmButtonText: 'Yes, update',
            cancelButtonText: 'Cancel'
        })
        if (!result.isConfirmed) return

        try {
            const updateData = { order_status: newStatus, updated_at: new Date().toISOString() }
            if (newStatus === 'completed') updateData.completed_at = new Date().toISOString()

            const { data, error } = await supabase
                .from('orders')
                .update(updateData)
                .eq('order_id', orderId)
                .select('order_id, order_status')

            if (error) throw error
            if (!data || data.length === 0) throw new Error('No rows updated.')

            if (newStatus === 'completed') {
                const { error: inventoryError } = await supabase
                    .rpc('consume_reserved_stock', { p_order_id: orderId })
                if (inventoryError) throw new Error(inventoryError.message)
            }

            await fetchOrders()
            Swal.fire({ icon: 'success', title: 'Status Updated', text: `Order moved to ${newStatus}.`, confirmButtonColor: '#FF6F3C', timer: 1800 })
        } catch (err) {
            console.error('Error updating order status:', err)
            Swal.fire({ icon: 'error', title: 'Failed to Update', text: err.message || 'An error occurred.', confirmButtonColor: '#FF6F3C' })
        }
    }

    const getStatusColor = (status) => {
        switch (status) {
            case 'pending': return 'bg-yellow-100 text-yellow-800'
            case 'preparing': return 'bg-blue-100 text-blue-800'
            case 'ready': return 'bg-purple-100 text-purple-800'
            case 'completed': return 'bg-green-100 text-green-800'
            case 'cancelled': return 'bg-red-100 text-red-800'
            default: return 'bg-gray-100 text-gray-800'
        }
    }

    const getColumnTheme = (status) => {
        switch (status) {
            case 'pending': return {
                accent: 'border-t-4 border-yellow-400',
                badge: 'bg-yellow-100 text-yellow-700',
                button: 'bg-yellow-400 hover:bg-yellow-500 text-white',
                countBg: 'bg-yellow-100 text-yellow-700'
            }
            case 'preparing': return {
                accent: 'border-t-4 border-blue-400',
                badge: 'bg-blue-100 text-blue-700',
                button: 'bg-blue-400 hover:bg-blue-500 text-white',
                countBg: 'bg-blue-100 text-blue-700'
            }
            case 'ready': return {
                accent: 'border-t-4 border-green-500',
                badge: 'bg-green-100 text-green-700',
                button: 'bg-green-500 hover:bg-green-600 text-white',
                countBg: 'bg-green-100 text-green-700'
            }
            case 'completed': return {
                accent: 'border-t-4 border-gray-400',
                badge: 'bg-gray-100 text-gray-700',
                button: 'bg-gray-300 hover:bg-gray-400 text-white',
                countBg: 'bg-gray-100 text-gray-700'
            }
            case 'cancelled': return {
                accent: 'border-t-4 border-red-300',
                badge: 'bg-red-100 text-red-700',
                button: 'bg-red-100 hover:bg-red-200 text-red-700',
                countBg: 'bg-red-100 text-red-700'
            }
            default: return {
                accent: 'border-t-4 border-gray-300',
                badge: 'bg-gray-100 text-gray-700',
                button: 'bg-gray-300 hover:bg-gray-400 text-white',
                countBg: 'bg-gray-100 text-gray-700'
            }
        }
    }

    // const getNextStatus = (currentStatus) => {
    //     const flow = { pending: 'preparing', preparing: 'ready', ready: 'completed', completed: null, cancelled: null }
    //     return flow[currentStatus] || null
    // }

    const formatBranchName = (name) =>
        name?.replace("Coffee Gus·to ", "") || ""

    const formatCurrency = (amount) =>
        `₱${parseFloat(amount || 0).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A'
        return new Date(dateString).toLocaleString('en-PH', { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
    }

    const formatCardTime = (dateString) => {
        if (!dateString) return '--:--'
        return new Date(dateString).toLocaleTimeString('en-PH', { hour: '2-digit', minute: '2-digit', hour12: false })
    }

    const formatFulfillmentType = (value) => {
        const map = { dine_in: 'Dine-In', takeout: 'Takeout', pickup: 'Pickup', delivery: 'Delivery' }
        return map[value] || 'N/A'
    }

    const formatOrderSource = (value) => {
        const map = { pos: 'POS', mobile_app: 'Mobile App' }
        return map[value] || 'N/A'
    }

    const filteredOrders = useMemo(() => {
        return orders.filter(order => {
            const search = searchQuery.toLowerCase()

            const matchesBranch =
                selectedBranchId === 'all' || order.branch_id === selectedBranchId

            const matchesSearch =
                String(order.reference_number || '').toLowerCase().includes(search) ||
                String(order.order_id || '').toLowerCase().includes(search) ||
                formatOrderSource(order.order_source).toLowerCase().includes(search) ||
                (order.order_source === 'mobile_app' ? 'mobile customer' : 'walk-in customer').includes(search)

            const matchesStatus =
                filterStatus === 'All' || order.order_status === filterStatus.toLowerCase()

            let matchesDate = true

            if (dateFilter !== 'all') {
                const orderDate = new Date(order.created_at)
                const now = new Date()

                if (dateFilter === 'day') {
                    matchesDate = orderDate.toDateString() === now.toDateString()
                }
                if (dateFilter === 'week') {
                    const startOfWeek = new Date(now)
                    startOfWeek.setDate(now.getDate() - now.getDay())
                    matchesDate = orderDate >= startOfWeek
                }
                if (dateFilter === 'month') {
                    matchesDate =
                        orderDate.getMonth() === now.getMonth() &&
                        orderDate.getFullYear() === now.getFullYear()
                }
                if (dateFilter === 'year') {
                    matchesDate = orderDate.getFullYear() === now.getFullYear()
                }
                if (dateFilter === 'custom' && customStart && customEnd) {
                    const from = new Date(customStart)
                    const to = new Date(customEnd)
                    to.setHours(23, 59, 59, 999)
                    matchesDate = orderDate >= from && orderDate <= to
                }
                if (dateFilter === 'custom' && (!customStart || !customEnd)) {
                    matchesDate = true
                }
            }

            return matchesBranch && matchesSearch && matchesStatus && matchesDate
        })
    }, [orders, selectedBranchId, searchQuery, filterStatus, dateFilter, customStart, customEnd])

    const groupedOrders = useMemo(() => ({
        pending: filteredOrders.filter(o => o.order_status === 'pending'),
        preparing: filteredOrders.filter(o => o.order_status === 'preparing'),
        ready: filteredOrders.filter(o => o.order_status === 'ready'),
        completed: filteredOrders.filter(o => o.order_status === 'completed'),
        cancelled: filteredOrders.filter(o => o.order_status === 'cancelled'),
    }), [filteredOrders])

    const visibleColumns = useMemo(() => {
        if (filterStatus === 'All') {
            return [
                { title: 'Pending', key: 'pending', orders: groupedOrders.pending },
                { title: 'Preparing', key: 'preparing', orders: groupedOrders.preparing },
                { title: 'Ready', key: 'ready', orders: groupedOrders.ready },
                { title: 'Completed', key: 'completed', orders: groupedOrders.completed },
            ]
        }
        const key = filterStatus.toLowerCase()
        return [{ title: filterStatus, key, orders: groupedOrders[key] || [] }]
    }, [filterStatus, groupedOrders])

    const stats = useMemo(() => {
        return {
            totalOrders: filteredOrders.length,
            pendingCount: filteredOrders.filter(o => o.order_status === 'pending').length,
            preparingCount: filteredOrders.filter(o => o.order_status === 'preparing').length,
            readyCount: filteredOrders.filter(o => o.order_status === 'ready').length,
            completedCount: filteredOrders.filter(o => o.order_status === 'completed').length,
            cancelledCount: filteredOrders.filter(o => o.order_status === 'cancelled').length,
            totalRevenue: filteredOrders
                .filter(o => o.order_status === 'completed')
                .reduce((s, o) => s + parseFloat(o.final_amount || 0), 0),
        }
    }, [filteredOrders])

    // Per-branch live summary 
    const branchSummary = useMemo(() => {
        const map = {}
        branches.forEach(b => {
            const branchOrders = orders.filter(o => o.branch_id === b.branch_id)
            map[b.branch_id] = {
                name: formatBranchName(b.name),
                pending: branchOrders.filter(o => o.order_status === 'pending').length,
                preparing: branchOrders.filter(o => o.order_status === 'preparing').length,
                ready: branchOrders.filter(o => o.order_status === 'ready').length,
                total: branchOrders.length,
            }
        })
        return map
    }, [orders, branches])

    const renderOrderCard = (order, theme) => (
        <div
            key={order.order_id}
            className={`bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden ${theme.accent.replace('border-t-4', 'border-l-4')}`}
        >
            <div className="px-3 py-2 border-b border-gray-100 flex items-center justify-between gap-2">
                <span className="text-[11px] font-semibold text-gray-700 flex-shrink-0">
                    #{order.reference_number || String(order.order_id).slice(0, 6)}
                </span>
                <div className="text-right">
                    <p className="text-[11px] text-gray-400 leading-tight">
                        {new Date(order.created_at).toLocaleDateString('en-PH', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </p>
                    <p className="text-[11px] text-gray-500 font-medium leading-tight">
                        {formatCardTime(order.created_at)}
                    </p>
                </div>
            </div>

            <div className="px-3 py-3 space-y-2">
                <div className="text-sm font-semibold text-gray-800">
                    {order.order_source === 'mobile_app' ? 'Mobile Customer' : 'Walk-in Customer'}
                </div>

                {/* Branch tag — always visible in admin view */}
                <div className="flex items-center gap-1.5">
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" className="text-[#FF6F3C] flex-shrink-0">
                        <path fill="currentColor" d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7m0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5s2.5 1.12 2.5 2.5s-1.12 2.5-2.5 2.5" />
                    </svg>
                    <span className="text-xs text-gray-500 truncate">{formatBranchName(order.branches?.name) || 'Unknown Branch'}</span>
                </div>

                <div className="flex items-center justify-between pt-1">
                    <span className={`px-2 py-1 rounded-full text-[11px] font-medium ${theme.badge}`}>
                        {order.order_status?.charAt(0).toUpperCase() + order.order_status?.slice(1)}
                    </span>
                    <span className="text-sm font-bold text-gray-800">{formatCurrency(order.final_amount)}</span>
                </div>
            </div>

            <div className="px-3 pb-3 flex gap-2">
                <button
                    onClick={() => handleViewOrder(order)}
                    className={`flex-1 py-2 rounded-md text-xs font-semibold transition-colors ${theme.button}`}
                >
                    View Details
                </button>
                {/* {getNextStatus(order.order_status) && (
                    <button
                        onClick={() => handleStatusChange(order.order_id, getNextStatus(order.order_status), order.branch_id)}
                        className="px-3 py-2 rounded-md text-xs font-semibold bg-gray-100 text-gray-700 hover:bg-gray-200"
                    >
                        Next
                    </button>
                )} */}
            </div>
        </div>
    )

    const renderOrderColumn = (title, statusKey, ordersList, page, setPage) => {
        const theme = getColumnTheme(statusKey)
        const pagedList = ordersList.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

        return (
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden flex flex-col h-auto max-h-[70vh] xl:h-[620px]">
                <div className={`px-4 py-3 ${theme.accent}`}>
                    <div className="flex items-center justify-between">
                        <h3 className="text-sm font-semibold text-gray-800">{title}</h3>
                        <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${theme.countBg}`}>{ordersList.length}</span>
                    </div>
                </div>
                <div className="p-3 space-y-3 flex-1 overflow-y-auto bg-gray-50/60">
                    {ordersList.length === 0 ? (
                        <div className="bg-white border border-dashed border-gray-200 rounded-lg p-4 text-center text-sm text-gray-400">No orders</div>
                    ) : (
                        pagedList.map(order => renderOrderCard(order, theme))
                    )}
                </div>
            </div>
        )
    }

    const renderFilteredStatusGrid = (title, statusKey, ordersList, page, setPage) => {
        const theme = getColumnTheme(statusKey)
        const pagedList = ordersList.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

        return (
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                <div className={`px-4 py-3 ${theme.accent}`}>
                    <div className="flex items-center justify-between">
                        <h3 className="text-sm font-semibold text-gray-800">{title}</h3>
                        <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${theme.countBg}`}>{ordersList.length}</span>
                    </div>
                </div>
                <div className="p-4 bg-gray-50/60 min-h-[520px]">
                    {ordersList.length === 0 ? (
                        <div className="bg-white border border-dashed border-gray-200 rounded-lg p-6 text-center text-sm text-gray-400">No orders</div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                            {pagedList.map(order => renderOrderCard(order, theme))}
                        </div>
                    )}
                </div>
                <Pagination
                    page={page}
                    totalItems={ordersList.length}
                    pageSize={PAGE_SIZE}
                    onPageChange={setPage}
                />
            </div>
        )
    }

    // ─── Pagination  ─────────────────────────────────────────────
    function Pagination({ page, totalItems, pageSize, onPageChange }) {
        const totalPages = Math.ceil(totalItems / pageSize)
        if (totalPages <= 1) return null

        return (
            <div className="flex items-center justify-between px-4 py-2 rounded-b-xl"
            >
                <span style={{ fontSize: 14, color: C.midgrey }}>
                    Showing <span className="font-medium text-gray-700">{Math.min((page - 1) * pageSize + 1, totalItems)}–{Math.min(page * pageSize, totalItems)}</span> of <span className="font-medium text-gray-700">{totalItems}</span> items
                </span>
                <div className="flex items-center gap-1">
                    <button
                        onClick={() => onPageChange(Math.max(1, page - 1))}
                        disabled={page === 1}
                        style={{
                            padding: "3px 10px", borderRadius: 8,
                            border: `1px solid ${C.border}`, background: C.card,
                            color: page === 1 ? C.border : C.textSoft,
                            fontSize: 14, cursor: page === 1 ? "not-allowed" : "pointer",
                        }}>
                        ← Prev
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                        .filter(n => n === 1 || n === totalPages || Math.abs(n - page) <= 1)
                        .reduce((acc, n, idx, arr) => {
                            if (idx > 0 && n - arr[idx - 1] > 1) acc.push("…")
                            acc.push(n)
                            return acc
                        }, [])
                        .map((n, idx) =>
                            n === "…" ? (
                                <span key={`e-${idx}`} style={{ fontSize: 16, color: C.midgrey, padding: "0 2px" }}>…</span>
                            ) : (
                                <button key={n} onClick={() => onPageChange(n)}
                                    style={{
                                        width: 28, height: 28, borderRadius: 8,
                                        border: `1px solid ${n === page ? C.nude : C.border}`,
                                        background: n === page ? C.nude : C.card,
                                        color: n === page ? "#fff" : C.textSoft,
                                        fontSize: 14, fontWeight: n === page ? 600 : 400,
                                        cursor: "pointer",
                                    }}>
                                    {n}
                                </button>
                            )
                        )
                    }
                    <button
                        onClick={() => onPageChange(Math.min(totalPages, page + 1))}
                        disabled={page === totalPages}
                        style={{
                            padding: "3px 10px", borderRadius: 8,
                            border: `1px solid ${C.border}`, background: C.card,
                            color: page === totalPages ? C.border : C.textSoft,
                            fontSize: 14, cursor: page === totalPages ? "not-allowed" : "pointer",
                        }}>
                        Next →
                    </button>
                </div>
            </div>
        )
    }

    const columnPageState = {
        pending: { page: pendingPage, setPage: setPendingPage },
        preparing: { page: preparingPage, setPage: setPreparingPage },
        ready: { page: readyPage, setPage: setReadyPage },
        completed: { page: completedPage, setPage: setCompletedPage },
        cancelled: { page: cancelledPage, setPage: setCancelledPage },
    }

    return (
        <div className="flex-1 overflow-y-auto bg-gray-50">
            {/* Header */}
            <Header
                title="Order Management"
                subtitle="Real-time order tracking across all branches"
                onGoToProfile={onGoToProfile}
            />

            <div className="p-6 bg-[#f6f6f7] min-h-screen">
                {/* Stats Row */}
                <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-4 mb-5">
                    {[
                        { label: 'Total Orders', value: stats.totalOrders, bg: 'bg-orange-100', text: 'text-orange-600', icon: <path fill="currentColor" d="M7 18c-1.1 0-1.99.9-1.99 2S5.9 22 7 22s2-.9 2-2s-.9-2-2-2M1 2v2h2l3.6 7.59l-1.35 2.45c-.16.28-.25.61-.25.96c0 1.1.9 2 2 2h12v-2H7.42c-.14 0-.25-.11-.25-.25l.03-.12l.9-1.63h7.45c.75 0 1.41-.41 1.75-1.03l3.58-6.49A1.003 1.003 0 0 0 20 4H5.21l-.94-2z" /> },
                        { label: 'Pending', value: stats.pendingCount, bg: 'bg-yellow-100', text: 'text-yellow-600', icon: <PendingIcon /> },
                        { label: 'Preparing', value: stats.preparingCount, bg: 'bg-blue-100', text: 'text-blue-600', icon: <PreparingIcon /> },
                        { label: 'Ready', value: stats.readyCount, bg: 'bg-green-100', text: 'text-green-600', icon: <path fill="currentColor" d="m9.55 18l-5.7-5.7l1.425-1.425l4.275 4.275l9.175-9.175L20.15 7.4z" /> },
                        { label: 'Completed', value: stats.completedCount, bg: 'bg-gray-100', text: 'text-gray-600', icon: <path fill="currentColor" d="m10.6 16.6l7.05-7.05l-1.4-1.4l-5.65 5.65l-2.85-2.85l-1.4 1.4zM12 22q-2.075 0-3.9-.788t-3.175-2.137T2.788 15.9T2 12t.788-3.9t2.137-3.175T8.1 2.788T12 2t3.9.788t3.175 2.137T21.213 8.1T22 12t-.788 3.9t-2.137 3.175t-3.175 2.138T12 22" /> },
                        { label: 'Cancelled', value: stats.cancelledCount, bg: 'bg-red-100', text: 'text-red-500', icon: <path fill="currentColor" d="M12 2C6.47 2 2 6.47 2 12s4.47 10 10 10s10-4.47 10-10S17.53 2 12 2m5 13.59L15.59 17L12 13.41L8.41 17L7 15.59L10.59 12L7 8.41L8.41 7L12 10.59L15.59 7L17 8.41L13.41 12z" /> },
                        { label: 'Total Revenue', value: formatCurrency(stats.totalRevenue), bg: 'bg-purple-100', text: 'text-purple-600', icon: <path fill="currentColor" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10s10-4.48 10-10S17.52 2 12 2m1.41 16.09V20h-2.67v-1.93c-1.71-.36-3.16-1.46-3.27-3.4h1.96c.1 1.05.82 1.87 2.65 1.87c1.96 0 2.4-.98 2.4-1.59c0-.83-.44-1.61-2.67-2.14c-2.48-.6-4.18-1.62-4.18-3.67c0-1.72 1.39-2.84 3.11-3.21V4h2.67v1.95c1.86.45 2.79 1.86 2.85 3.39H14.3c-.05-1.11-.64-1.87-2.22-1.87c-1.5 0-2.4.68-2.4 1.64c0 .84.65 1.39 2.67 1.91s4.18 1.39 4.18 3.91c-.01 1.83-1.38 2.83-3.12 3.16" /> },
                    ].map(({ label, value, bg, text, icon }) => (
                        <div key={label} className="bg-white rounded-xl border border-gray-200 shadow-sm px-4 py-4 flex flex-col items-start gap-2">
                            <div className={`w-9 h-9 rounded-lg ${bg} ${text} flex items-center justify-center`}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24">{icon}</svg>
                            </div>
                            <p className="text-[11px] text-gray-500 leading-tight">{label}</p>
                            {loading ? (
                                <SkeletonBlock className="h-6 w-12" />
                            ) : (
                                <p className="text-xl font-bold text-gray-800 leading-none">{value}</p>
                            )}
                        </div>
                    ))}
                </div>

                {/* Branch Live Summary Pills */}
                {branches.length > 0 && (
                    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 mb-5">
                        <div className="flex items-center gap-2 mb-3">
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" className="text-[#FF6F3C]">
                                <path fill="currentColor" d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7m0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5s2.5 1.12 2.5 2.5s-1.12 2.5-2.5 2.5" />
                            </svg>
                            <h2 className="text-sm font-semibold text-gray-700">Live Branch Overview</h2>
                        </div>
                        <div className="flex gap-2 overflow-x-auto pb-1"
                            onMouseDown={handleDragMouseDown}
                            onMouseMove={handleDragMouseMove}
                            onMouseUp={handleDragMouseUpOrLeave}
                            onMouseLeave={handleDragMouseUpOrLeave}
                            onWheel={handleHorizontalWheelScroll}
                        >
                            <button
                                onClick={() => setSelectedBranchId('all')}
                                className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-medium transition-all flex-shrink-0 whitespace-nowrap ${selectedBranchId === 'all'
                                        ? 'bg-[#FF6F3C] border-[#FF6F3C] text-white shadow-sm'
                                        : 'bg-white border-gray-200 text-gray-700 hover:border-[#FF6F3C] hover:text-[#FF6F3C]'
                                    }`}
                            >
                                <span>All Branches</span>
                                <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${selectedBranchId === 'all' ? 'bg-white text-[#FF6F3C]' : 'bg-gray-100 text-gray-600'}`}>
                                    {loading ? <SkeletonBlock className="w-3 h-2.5 inline-block" /> : orders.length}
                                </span>
                            </button>

                            {branches.map(branch => {
                                const summary = branchSummary[branch.branch_id]
                                const isSelected = selectedBranchId === branch.branch_id
                                const hasActive = (summary?.pending || 0) + (summary?.preparing || 0) > 0
                                return (
                                    <button
                                        key={branch.branch_id}
                                        onClick={() => setSelectedBranchId(branch.branch_id)}
                                        className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-medium transition-all flex-shrink-0 whitespace-nowrap ${isSelected
                                                ? 'bg-[#FF6F3C] border-[#FF6F3C] text-white shadow-sm'
                                                : 'bg-white border-gray-200 text-gray-700 hover:border-[#FF6F3C] hover:text-[#FF6F3C]'
                                            }`}
                                    >
                                        <span className="flex items-center gap-1.5">
                                            {hasActive && !isSelected && (
                                                <span className="w-1.5 h-1.5 rounded-full bg-yellow-400 animate-pulse flex-shrink-0"></span>
                                            )}
                                            {formatBranchName(branch.name)}
                                        </span>
                                        <div className="flex items-center gap-1">
                                            {loading ? (
                                                <SkeletonBlock className="w-6 h-3.5 rounded-full" />
                                            ) : (
                                                <>
                                                    {summary?.pending > 0 && (
                                                        <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${isSelected ? 'bg-yellow-200 text-yellow-800' : 'bg-yellow-100 text-yellow-700'}`}>
                                                            {summary.pending}
                                                        </span>
                                                    )}
                                                    {summary?.preparing > 0 && (
                                                        <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${isSelected ? 'bg-blue-200 text-blue-800' : 'bg-blue-100 text-blue-700'}`}>
                                                            {summary.preparing}
                                                        </span>
                                                    )}
                                                    {summary?.ready > 0 && (
                                                        <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${isSelected ? 'bg-green-200 text-green-800' : 'bg-green-100 text-green-700'}`}>
                                                            {summary.ready}
                                                        </span>
                                                    )}
                                                    {!summary?.pending && !summary?.preparing && !summary?.ready && (
                                                        <span className={`px-1.5 py-0.5 rounded-full text-[10px] ${isSelected ? 'bg-white/30 text-white' : 'bg-gray-100 text-gray-500'}`}>
                                                            {summary?.total || 0}
                                                        </span>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    </button>
                                )
                            })}
                        </div>
                    </div>
                )}

                {/* Search & Status Filter Bar */}
                <div className="sticky top-0 z-30 bg-white rounded-xl border border-gray-200 shadow-sm p-4 mb-5">
                    <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-3">
                        <div className="flex flex-col md:flex-row gap-3 flex-1">
                            <div className="relative w-full md:max-w-xs">
                                <input
                                    type="text"
                                    placeholder="Search order # ..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF6F3C] text-sm"
                                />
                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24">
                                        <path fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10a7 7 0 1 0 14 0a7 7 0 1 0-14 0m18 11l-6-6" />
                                    </svg>
                                </span>
                            </div>

                            <select
                                value={filterStatus}
                                onChange={(e) => setFilterStatus(e.target.value)}
                                className="px-4 py-2.5 border border-gray-300 rounded-lg bg-white text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#FF6F3C]"
                            >
                                <option value="All">All Statuses</option>
                                <option value="Pending">Pending</option>
                                <option value="Preparing">Preparing</option>
                                <option value="Ready">Ready</option>
                                <option value="Completed">Completed</option>
                                <option value="Cancelled">Cancelled</option>
                            </select>

                            <select
                                value={dateFilter}
                                onChange={(e) => {
                                    setDateFilter(e.target.value)
                                    if (e.target.value !== 'custom') {
                                        setCustomStart('')
                                        setCustomEnd('')
                                    }
                                }}
                                className="px-4 py-2.5 border border-gray-300 rounded-lg bg-white text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#FF6F3C]"
                            >
                                <option value="all">All Time</option>
                                <option value="day">Today</option>
                                <option value="week">This Week</option>
                                <option value="month">This Month</option>
                                <option value="year">This Year</option>
                                <option value="custom">Custom Range</option>
                            </select>

                            {dateFilter === 'custom' && (
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="text-xs text-gray-400">From:</span>
                                    <input
                                        type="date"
                                        value={customStart}
                                        max={customEnd || new Date().toISOString().split('T')[0]}
                                        onChange={(e) => setCustomStart(e.target.value)}
                                        className="px-3 py-2.5 border rounded-lg bg-white text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#FF6F3C]"
                                        style={{ borderColor: customStart && customEnd ? '#FF6F3C' : '#D1D5DB' }}
                                    />
                                    <span className="text-xs text-gray-400">To</span>
                                    <input
                                        type="date"
                                        value={customEnd}
                                        min={customStart}
                                        max={new Date().toISOString().split('T')[0]}
                                        onChange={(e) => setCustomEnd(e.target.value)}
                                        className="px-3 py-2.5 border rounded-lg bg-white text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#FF6F3C]"
                                        style={{ borderColor: customStart && customEnd ? '#FF6F3C' : '#D1D5DB' }}
                                    />
                                    {customStart && customEnd && (
                                        <button
                                            onClick={() => { setCustomStart(''); setCustomEnd('') }}
                                            className="px-2.5 py-2 rounded-lg border border-gray-200 text-xs text-gray-500 hover:bg-gray-50"
                                        >
                                            ✕ Clear
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>

                        <div className="text-sm text-gray-500 font-medium flex items-center gap-1.5">
                            {loading ? (
                                <>Loading orders<SkeletonBlock className="w-4 h-3 inline-block" /></>
                            ) : selectedBranchId === 'all' ? (
                                `Showing all ${filteredOrders.length} orders across ${branches.length} branches`
                            ) : (
                                `${formatBranchName(branches.find(b => b.branch_id === selectedBranchId)?.name) || 'Branch'} — ${filteredOrders.length} orders`
                            )}
                        </div>
                    </div>
                </div>

                {/* Loading State */}
                {loading ? (
                    <div className="flex items-center justify-center py-20">
                        <div className="text-center">
                            <div className="w-10 h-10 border-4 border-[#FF6F3C] border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
                            <p className="text-sm text-gray-500">Loading orders...</p>
                        </div>
                    </div>
                ) : filterStatus === 'All' ? (
                    <div className="grid grid-cols-1 xl:grid-cols-4 gap-4">
                        {visibleColumns.map((col) => (
                            <div key={col.key}>
                                {renderOrderColumn(col.title, col.key, col.orders, columnPageState[col.key].page, columnPageState[col.key].setPage)}
                            </div>
                        ))}
                    </div>
                ) : (
                    renderFilteredStatusGrid(
                        visibleColumns[0]?.title || filterStatus,
                        visibleColumns[0]?.key || filterStatus.toLowerCase(),
                        visibleColumns[0]?.orders || [],
                        filteredPage,
                        setFilteredPage
                    )
                )}
            </div>

            {/* View Order Modal */}
            {showViewModal && viewingOrder && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div
                        className="absolute inset-0 bg-black bg-opacity-50"
                        onClick={() => { setShowViewModal(false); setViewingOrder(null); setOrderItems([]) }}
                    />
                    <div className="relative bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="border-b border-gray-200 px-6 py-4 flex items-start justify-between">
                            <div>
                                <h2 className="text-xl font-bold text-gray-800">Order Details</h2>
                                <p className="text-sm text-gray-600 mt-0.5">Order #{viewingOrder.reference_number || 'No Reference'}</p>
                            </div>
                            {/* Branch badge in modal header */}
                            <div className="flex items-center gap-1.5 bg-orange-50 border border-orange-200 rounded-lg px-3 py-1.5">
                                <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" className="text-[#FF6F3C]">
                                    <path fill="currentColor" d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7m0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5s2.5 1.12 2.5 2.5s-1.12 2.5-2.5 2.5" />
                                </svg>
                                <span className="text-xs font-medium text-[#FF6F3C]">{formatBranchName(viewingOrder.branches?.name) || 'Unknown Branch'}</span>
                            </div>
                        </div>

                        <div className="px-6 py-6 space-y-6">
                            {/* Order Info Grid */}
                            <div>
                                <h3 className="text-lg font-semibold text-gray-800 mb-4">Order Information</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-600 mb-1">Order ID</label>
                                        <p className="text-gray-800 font-mono text-sm">{viewingOrder.order_id}</p>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-600 mb-1">Reference Number</label>
                                        <p className="text-gray-800 font-medium">{viewingOrder.reference_number || 'N/A'}</p>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-600 mb-1">Status</label>
                                        <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(viewingOrder.order_status)}`}>
                                            {viewingOrder.order_status?.charAt(0).toUpperCase() + viewingOrder.order_status?.slice(1)}
                                        </span>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-600 mb-1">Branch</label>
                                        <p className="text-gray-800">
                                            {formatBranchName(viewingOrder.branches?.name) || 'N/A'}
                                        </p>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-600 mb-1">Order Source</label>
                                        <p className="text-gray-800">{formatOrderSource(viewingOrder.order_source)}</p>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-600 mb-1">Fulfillment Type</label>
                                        <p className="text-gray-800">{formatFulfillmentType(viewingOrder.fulfillment_type)}</p>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-600 mb-1">Created At</label>
                                        <p className="text-gray-800">{formatDate(viewingOrder.created_at)}</p>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-600 mb-1">Completed At</label>
                                        <p className="text-gray-800">{viewingOrder.completed_at ? formatDate(viewingOrder.completed_at) : '—'}</p>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-600 mb-1">Barista</label>
                                        <p className="text-gray-800">
                                            {viewingOrder.barista_info
                                                ? `${viewingOrder.barista_info.first_name} ${viewingOrder.barista_info.last_name}`
                                                : '—'}
                                        </p>
                                    </div>
                                </div>

                                {viewingOrder.customer_notes && (
                                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mt-4">
                                        <p className="text-sm font-semibold text-amber-800 mb-1">Customer Notes</p>
                                        <p className="text-sm text-amber-700">{viewingOrder.customer_notes}</p>
                                    </div>
                                )}

                                {viewingOrder.fulfillment_type === 'delivery' && (
                                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
                                        <h3 className="text-sm font-semibold text-blue-800 mb-3">Delivery Information</h3>
                                        {(viewingOrder.customer || viewingOrder.delivery_address) ? (
                                            <div className="space-y-3">
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                    <div>
                                                        <label className="block text-xs font-medium text-blue-600 mb-0.5">Customer Name</label>
                                                        <p className="text-sm text-gray-800">
                                                            {viewingOrder.customer?.first_name} {viewingOrder.customer?.last_name}
                                                        </p>
                                                    </div>
                                                    <div>
                                                        <label className="block text-xs font-medium text-blue-600 mb-0.5">Phone Number</label>
                                                        <p className="text-sm text-gray-800">{viewingOrder.customer?.phone_number || '—'}</p>
                                                    </div>
                                                    <div>
                                                        <label className="block text-xs font-medium text-blue-600 mb-0.5">Email</label>
                                                        <p className="text-sm text-gray-800">{viewingOrder.customer?.email || '—'}</p>
                                                    </div>
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-medium text-blue-600 mb-1">Delivery Address</label>
                                                    {viewingOrder.delivery_address ? (
                                                        <div className="bg-white border border-blue-100 rounded-md px-3 py-2">
                                                            <div className="flex items-center gap-2">
                                                                <span className="text-xs font-semibold text-gray-700">{viewingOrder.delivery_address.label}</span>
                                                            </div>
                                                            <p className="text-sm text-gray-600 mt-0.5">{viewingOrder.delivery_address.full_address}</p>
                                                        </div>
                                                    ) : (
                                                        <p className="text-sm text-gray-500">No address recorded for this order.</p>
                                                    )}
                                                </div>
                                                {viewingOrder.customer_notes && (
                                                    <div>
                                                        <label className="block text-xs font-medium text-blue-600 mb-1">Customer Notes</label>
                                                        <p className="text-sm text-gray-700 bg-white border border-blue-100 rounded-md px-3 py-2">
                                                            {viewingOrder.customer_notes}
                                                        </p>
                                                    </div>
                                                )}
                                            </div>
                                        ) : (
                                            <p className="text-sm text-blue-600">No customer record found.</p>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Order Items */}
                            <div>
                                <h3 className="text-lg font-semibold text-gray-800 mb-4">Order Items</h3>
                                {viewingOrder.customer_notes && (
                                    <div className="mb-4 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-700">
                                        <span className="font-semibold">Note:</span> {viewingOrder.customer_notes}
                                    </div>
                                )}
                                {(() => {
                                    const activeItems = orderItems.filter(
                                        item => !['voided', 'void_approved', 'void_requested'].includes(item.line_status)
                                    )
                                    const voidedItems = orderItems.filter(
                                        item => ['voided', 'void_approved', 'void_requested'].includes(item.line_status)
                                    )

                                    const tableHeader = (
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Item</th>
                                                <th className="px-4 py-2 text-center text-xs font-medium text-gray-500">Qty</th>
                                                <th className="px-4 py-2 text-center text-xs font-medium text-gray-500">Prep Status</th>
                                                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Price</th>
                                                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Total</th>
                                            </tr>
                                        </thead>
                                    )

                                    return (
                                        <div className="space-y-4">
                                            {/* Active items */}
                                            {activeItems.length > 0 ? (
                                                <div className="border border-gray-200 rounded-lg overflow-hidden overflow-x-auto">
                                                    <table className="w-full" style={{ minWidth: 640 }}>
                                                        {tableHeader}
                                                        <tbody className="divide-y divide-gray-200">
                                                            {activeItems.map((item, index) => (
                                                                <tr key={index}>
                                                                    <td className="px-4 py-3 text-sm text-gray-800">
                                                                        <div className="flex items-center gap-3">
                                                                            {item.menu_items?.item_img && (
                                                                                <img src={item.menu_items.item_img} alt={item.menu_items.name} className="w-10 h-10 rounded-lg object-cover" />
                                                                            )}
                                                                            <div>
                                                                                <p className="font-medium">{item.menu_items?.name || 'Unknown Item'}</p>
                                                                                {item.menu_items?.category && (
                                                                                    <p className="text-xs text-gray-500">
                                                                                        {item.menu_items.category}
                                                                                        {item.menu_items?.classification && (
                                                                                            <span className="ml-1 text-gray-400">· {item.menu_items.classification}</span>
                                                                                        )}
                                                                                    </p>
                                                                                )}
                                                                                {item.order_item_variants?.length > 0 && (
                                                                                    <div className="mt-1 space-y-0.5">
                                                                                        {item.order_item_variants.map((v, i) => (
                                                                                            <p key={i} className="text-xs text-gray-500">
                                                                                                <span className="font-medium text-gray-600">{v.item_variants?.category || 'Variant'}:</span>{' '}
                                                                                                {v.item_variants?.variant_name || 'Unnamed'}
                                                                                            </p>
                                                                                        ))}
                                                                                    </div>
                                                                                )}
                                                                                {item.order_item_modifiers?.length > 0 && (
                                                                                    <div className="mt-1 space-y-0.5">
                                                                                        {item.order_item_modifiers.map((m, i) => (
                                                                                            <p key={i} className="text-xs text-[#A0826D]">
                                                                                                + {m.item_modifiers?.modifier_name || 'Unnamed Add-on'}
                                                                                            </p>
                                                                                        ))}
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    </td>
                                                                    <td className="px-4 py-3 text-sm text-gray-600 text-center">{item.quantity}</td>
                                                                    <td className="px-4 py-3 text-sm text-center">
                                                                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${item.prep_status === 'completed'
                                                                                ? 'bg-gray-100 text-gray-600'
                                                                                : item.prep_status === 'ready'
                                                                                    ? 'bg-green-100 text-green-800'
                                                                                    : 'bg-yellow-100 text-yellow-800'
                                                                            }`}>
                                                                            {item.prep_status === 'completed'
                                                                                ? 'Completed'
                                                                                : item.prep_status === 'ready'
                                                                                    ? 'Ready'
                                                                                    : 'Pending'}
                                                                        </span>
                                                                    </td>
                                                                    <td className="px-4 py-3 text-sm text-gray-600 text-right">{formatCurrency(item.item_price)}</td>
                                                                    <td className="px-4 py-3 text-sm font-medium text-gray-800 text-right">
                                                                        {formatCurrency(parseFloat(item.item_price || 0) * parseFloat(item.quantity || 0))}
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            ) : (
                                                <p className="text-gray-500 text-sm">No active items found for this order.</p>
                                            )}

                                            {/* Voided items collapsible */}
                                            {voidedItems.length > 0 && (
                                                <div className="border border-red-100 rounded-lg overflow-hidden">
                                                    <button
                                                        onClick={() => setShowVoidedItems(v => !v)}
                                                        className="w-full flex items-center justify-between px-4 py-3 bg-red-50 hover:bg-red-100 transition-colors"
                                                    >
                                                        <div className="flex items-center gap-2">
                                                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-red-500">
                                                                <circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" />
                                                            </svg>
                                                            <span className="text-sm font-semibold text-red-700">Voided Items</span>
                                                            <span className="bg-red-100 text-red-700 text-xs font-semibold px-2 py-0.5 rounded-full border border-red-200">
                                                                {voidedItems.length}
                                                            </span>
                                                        </div>
                                                        <svg
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            width="16" height="16"
                                                            viewBox="0 0 24 24"
                                                            fill="none"
                                                            stroke="currentColor"
                                                            strokeWidth="2"
                                                            className={`text-red-400 transition-transform duration-200 ${showVoidedItems ? 'rotate-180' : ''}`}
                                                        >
                                                            <path d="M6 9l6 6 6-6" />
                                                        </svg>
                                                    </button>

                                                    {showVoidedItems && (
                                                        <div className="overflow-x-auto">
                                                            <table className="w-full">
                                                                <thead className="bg-red-50/60">
                                                                    <tr>
                                                                        <th className="px-4 py-2 text-left text-xs font-medium text-red-400">Item</th>
                                                                        <th className="px-4 py-2 text-center text-xs font-medium text-red-400">Qty</th>
                                                                        <th className="px-4 py-2 text-center text-xs font-medium text-red-400">Void Reason</th>
                                                                        <th className="px-4 py-2 text-right text-xs font-medium text-red-400">Price</th>
                                                                        <th className="px-4 py-2 text-right text-xs font-medium text-red-400">Total</th>
                                                                        <th className="px-4 py-2 text-center text-xs font-medium text-red-400">Status</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody className="divide-y divide-red-50">
                                                                    {voidedItems.map((item, index) => (
                                                                        <tr key={index} className="opacity-60">
                                                                            <td className="px-4 py-3 text-sm text-gray-500">
                                                                                <div className="flex items-center gap-3">
                                                                                    {item.menu_items?.item_img && (
                                                                                        <img
                                                                                            src={item.menu_items.item_img}
                                                                                            alt={item.menu_items.name}
                                                                                            className="w-10 h-10 rounded-lg object-cover grayscale"
                                                                                        />
                                                                                    )}
                                                                                    <div>
                                                                                        <p className="font-medium line-through text-gray-400">
                                                                                            {item.menu_items?.name || 'Unknown Item'}
                                                                                        </p>
                                                                                        {item.order_item_variants?.length > 0 && (
                                                                                            <div className="mt-1 space-y-0.5">
                                                                                                {item.order_item_variants.map((v, i) => (
                                                                                                    <p key={i} className="text-xs text-gray-400">
                                                                                                        {v.item_variants?.variant_name}
                                                                                                    </p>
                                                                                                ))}
                                                                                            </div>
                                                                                        )}
                                                                                        {item.order_item_modifiers?.length > 0 && (
                                                                                            <div className="mt-1 space-y-0.5">
                                                                                                {item.order_item_modifiers.map((m, i) => (
                                                                                                    <p key={i} className="text-xs text-gray-400">
                                                                                                        + {m.item_modifiers?.modifier_name}
                                                                                                    </p>
                                                                                                ))}
                                                                                            </div>
                                                                                        )}
                                                                                    </div>
                                                                                </div>
                                                                            </td>
                                                                            <td className="px-4 py-3 text-sm text-gray-400 text-center">{item.quantity}</td>
                                                                            <td className="px-4 py-3 text-center">
                                                                                <span className="text-xs text-gray-400 italic">
                                                                                    {item.void_reason
                                                                                        ? item.void_reason.replace(/_/g, ' ')
                                                                                        : '—'}
                                                                                </span>
                                                                            </td>
                                                                            <td className="px-4 py-3 text-sm text-gray-400 text-right line-through">
                                                                                {formatCurrency(item.item_price)}
                                                                            </td>
                                                                            <td className="px-4 py-3 text-sm text-gray-400 text-right line-through">
                                                                                {formatCurrency(parseFloat(item.item_price || 0) * parseFloat(item.quantity || 0))}
                                                                            </td>
                                                                            <td className="px-4 py-3 text-center">
                                                                                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
                                                                                    {item.line_status === 'void_approved'
                                                                                        ? 'Void Approved'
                                                                                        : item.line_status === 'void_requested'
                                                                                            ? 'Void Requested'
                                                                                            : 'Voided'}
                                                                                </span>
                                                                            </td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    )
                                })()}
                            </div>

                            {/* Payment Summary */}
                            <div>
                                <h3 className="text-lg font-semibold text-gray-800 mb-4">Payment Summary</h3>
                                <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Subtotal</span>
                                        <span className="font-medium">{formatCurrency(viewingOrder.subtotal)}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">VAT (12%) Included Tax</span>
                                        <span className="font-medium">{formatCurrency(viewingOrder.tax)}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Discount</span>
                                        <span className="font-medium text-green-600">-{formatCurrency(viewingOrder.discount)}</span>
                                    </div>
                                    <div className="border-t border-gray-300 pt-2 flex justify-between">
                                        <span className="text-lg font-bold">Total</span>
                                        <span className="text-lg font-bold text-[#FF6F3C]">{formatCurrency(viewingOrder.final_amount)}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
                            <button
                                onClick={() => {
                                    setShowViewModal(false);
                                    setViewingOrder(null);
                                    setOrderItems([]);
                                    setShowVoidedItems(false);
                                }}
                                className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300"
                            >
                                Close
                            </button>
                            {/* {getNextStatus(viewingOrder.order_status) && (
                                <button
                                    onClick={() => {
                                        handleStatusChange(viewingOrder.order_id, getNextStatus(viewingOrder.order_status), viewingOrder.branch_id)
                                        setShowViewModal(false)
                                        setViewingOrder(null)
                                        setOrderItems([])
                                    }}
                                    className="px-6 py-2 bg-[#FF6F3C] text-white rounded-lg font-medium hover:bg-[#ff4c0b]"
                                >
                                    Move to {getNextStatus(viewingOrder.order_status)?.charAt(0).toUpperCase() + getNextStatus(viewingOrder.order_status)?.slice(1)}
                                </button>
                            )} */}
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}